import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Divider,
  Grid,
  Typography,
  CircularProgress,
  styled,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  LabelList
} from 'recharts';
import { taskDataService, normalizeBranchesId } from '../../services/taskDataService';
import { surveyDataService } from '../../services/surveyDataService';
import firebaseDataService from '../../services/FirebaseDataService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import GuideButton from '../common/guide/GuideButton';

// Tema renkleri
const SURVEY_COLORS = {
  positive: '#4CAF50', // Yeşil
  neutral: '#9E9E9E',  // Gri
  negative: '#F44336', // Kırmızı
};

// Normal Görev Durum Renkleri
const NORMAL_TASK_STATUS_COLORS = {
  pending: '#FFA000',    // Turuncu
  accepted: '#1976D2',   // Mavi
  started: '#43A047',    // Yeşil
  completed: '#6A1B9A',  // Mor
};

// Chart Container kullanımı Dashboard ile aynı olsun
const ChartContainer = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 8,
  '& .MuiTypography-h6': {
    fontSize: '1rem',
    fontWeight: 'bold',
  },
}));

// SurveyCharts bileşeni için prop arayüzü
interface SurveyChartsProps {
  branchId?: string | null;
  isManager?: boolean;
  selectedWidgets?: string[];
  showBranchFilter?: boolean; // Şube filtresini gösterme/gizleme
}

// Anket detaylı istatistikleri için interface
interface SurveyDetailedStats {
  surveyId: string;
  surveyTitle: string;
  totalResponses: number; // Toplam cevap sayısı (soru bazında)
  totalParticipations: number; // Toplam anket katılımı sayısı
  satisfactionRate: number; // Memnuniyet oranı (olumlu cevap %)
  dissatisfactionRate: number; // Olumsuz geri bildirim oranı (%)
  neutralRate: number; // Nötr cevap oranı (%)
  feedbackCount: number; // Yorum/öneri sayısı (açık uçlu)
  lastResponseDate: number; // Son cevap tarihi
  averageResponsesPerParticipation: number; // Katılım başına ortalama cevap sayısı
}

const SurveyCharts: React.FC<SurveyChartsProps> = ({ branchId, isManager = false, selectedWidgets, showBranchFilter = false }) => {
  const { currentUser, userDetails } = useAuth();
  const { t } = useTranslation('common2');
  const [loading, setLoading] = useState(true);
  const [personnel, setPersonnel] = useState<any[]>([]);
  const [tasks, setTasks] = useState<Record<string, any>>({});
  const [overallStatsData, setOverallStatsData] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);
  const theme = useTheme();

  // Normal görevler için state'ler
  const [normalTasks, setNormalTasks] = useState<any[]>([]);
  const [normalTaskStatsData, setNormalTaskStatsData] = useState<any[]>([]);
  
  // Normal görev performans verisi için yeni state
  const [normalTaskPersonnelPerformanceData, setNormalTaskPersonnelPerformanceData] = useState<any[]>([]);

  // Anket detaylı istatistikleri için state'ler
  const [surveyDetailedStats, setSurveyDetailedStats] = useState<SurveyDetailedStats[]>([]);

  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { branches: allBranches } = useAuth();
  const branches = allBranches; // Compatibility için aynı isimde tut
  
  // Şube filtresi için state'ler (sadece showBranchFilter true ise)
  const [selectedBranchFilter, setSelectedBranchFilter] = useState<string>('all');
  
  // Prop'tan gelen selectedWidgets'ı kontrol et, yoksa varsayılan olarak tümünü göster
  const widgetsToShow = selectedWidgets || ['normalTaskStatusChart', 'normalTaskPersonnelPerformance', 'overallSurveySatisfaction', 'surveyTrend'];
  
  // Eğer Dashboard'dan 'surveyCharts' widget'ı ile çağrılıyorsa, sadece anket grafiklerini göster
  const finalWidgetsToShow = selectedWidgets && selectedWidgets.includes('surveyCharts') 
    ? ['overallSurveySatisfaction', 'surveyTrend', 'surveyDetailedStats'] 
    : widgetsToShow;

  // Şubeleri yükle (sadece showBranchFilter true ise)
  useEffect(() => {
    if (!showBranchFilter || !userDetails?.companyId) {
      return;
    }

    // ✅ Şubeler artık AuthContext'ten otomatik geliyor
  }, [showBranchFilter, userDetails]);

  // Personel verilerini yükle (şube filtreleme için gerekli)
  useEffect(() => {
    if (!userDetails?.companyId) {
      return;
    }

    const companyId = userDetails.companyId;
    const fetchPersonnel = async () => {
      try {
        const personnelResult = await firebaseDataService.getPersonnel(companyId);
        const personnelList = Array.isArray(personnelResult) ? personnelResult : (personnelResult ? [personnelResult] : []);

        setPersonnel(personnelList.map((p: any) => ({
          id: p.id,
          name: p.name,
          branchesId: p.branchesId ?? null
        })));
      } catch (error) {
        console.error("Personel verileri yüklenirken hata:", error);
      }
    };
    
    fetchPersonnel();
  }, [userDetails]);

  // Görev verilerini yükle (şube filtreleme için gerekli)
  // Ayrıca normal görevleri de buradan alacağız
  useEffect(() => {
    if (!userDetails?.companyId) {
      return;
    }

    const fetchTasks = async () => {
      try {
        // Faz 5 DILIM 8: görevler SQL'den (taskDataService). Anket lookup'ı
        // answeredSurveys taskId (legacyFirebaseKey) ile yapıldığından tasks
        // bu anahtarla saklanır; her görevde personnelId = personnelFirebaseUid.
        const taskItems: any[] = [];
        let tpage = 1;
        for (;;) {
          const res = await taskDataService.listTasks({ page: tpage, pageSize: 500 });
          taskItems.push(...res.items);
          if (res.items.length === 0 || taskItems.length >= (res.totalCount || 0)) break;
          tpage++;
        }

        if (taskItems.length > 0) {
          const normalizeTask = (task: any) => ({
            ...task,
            id: task.legacyFirebaseKey || String(task.id),
            personnelId: task.personnelFirebaseUid || '',
            branchesId: normalizeBranchesId(task.branchesId),
          });

          const allTasksData: Record<string, any> = {};
          taskItems.forEach((task: any) => {
            const n = normalizeTask(task);
            allTasksData[n.id] = n;
          });
          setTasks(allTasksData); // Mevcut görevleri ayarla (anketler için)

          // Normal görevleri filtrele (isRecurring olmayanlar)
          const filteredNormalTasks = Object.values(allTasksData)
            .map((task: any) => {
              // Personel ismini ekle
              let personnelName = 'Atanmamış';
              if (task.personnelId && personnel.length > 0) {
                const person = personnel.find(p => p.id === task.personnelId);
                if (person) {
                  personnelName = person.name;
                }
              }
              
              return { 
                ...task, 
                personnelName // Personel ismini ekle
              };
            })
            .filter(task => !task.isRecurring);

          // Şube yöneticisi ise, sadece kendi şubesindeki normal görevleri filtrele
          // VEYA şube filtresi seçiliyse sadece o şubeye ait görevleri filtrele
          const effectiveTaskBranchId = isManager && branchId 
            ? branchId 
            : (showBranchFilter && selectedBranchFilter !== 'all' ? selectedBranchFilter : null);
          
          if (effectiveTaskBranchId) {
            const branchFilteredTasks = filteredNormalTasks.filter(task => {
              // Şube ID'si varsa doğrudan kontrol et
              if (task.branchesId === effectiveTaskBranchId) {
                return true;
              }
              // Personel ID'siyle şubeyi kontrol et (eğer task'ta personnelId varsa)
              if (task.personnelId) {
                const person = personnel.find(p => p.id === task.personnelId);
                return person && person.branchesId === effectiveTaskBranchId;
              }
              return false;
            });
            setNormalTasks(branchFilteredTasks);
            
            const taskFilterType = isManager && branchId ? 'Yönetici şubesi' : 'Manuel şube filtresi';
            console.log(`Normal görevler ${taskFilterType} filtrelendi: ${branchFilteredTasks.length} görev`);
          } else {
            setNormalTasks(filteredNormalTasks);
            console.log(`Tüm normal görevler yüklendi: ${filteredNormalTasks.length} görev`);
          }

        }
      } catch (error) {
        console.error("Görev verileri yüklenirken hata:", error);
      }
    };

    fetchTasks();
  }, [userDetails, branchId, isManager, personnel, showBranchFilter, selectedBranchFilter]); // personnel bağımlılığa eklendi

  // Anket istatistiklerini SQL stats uçlarından yükle (Faz 8). Sube filtresi
  // server-side (branchId) yapilir; sahip adi backend JOIN'inden gelir.
  useEffect(() => {
    if (!userDetails?.companyId) {
      setLoading(false);
      return;
    }

    const effectiveBranchId = isManager && branchId
      ? branchId
      : (showBranchFilter && selectedBranchFilter !== 'all' ? selectedBranchFilter : null);

    const fetchSurveyStats = async () => {
      setLoading(true);
      try {
        const filter = effectiveBranchId ? { branchId: effectiveBranchId } : {};

        const [summary, trend, bySurvey] = await Promise.all([
          surveyDataService.getSummary(filter),
          surveyDataService.getTrend({ ...filter, weeks: 4 }),
          surveyDataService.getBySurvey(filter),
        ]);

        setOverallStatsData(summary.total > 0 ? [
          { name: 'Olumlu', value: summary.positive, color: SURVEY_COLORS.positive },
          { name: 'Nötr', value: summary.neutral, color: SURVEY_COLORS.neutral },
          { name: 'Olumsuz', value: summary.negative, color: SURVEY_COLORS.negative },
        ] : []);

        setTrendData((trend || []).map(p => ({
          week: p.week,
          positivePercent: p.positivePercent,
          neutralPercent: p.neutralPercent,
          negativePercent: p.negativePercent,
          total: p.total,
        })));

        setSurveyDetailedStats((bySurvey || [])
          .map(s => ({
            surveyId: String(s.surveyId),
            surveyTitle: s.surveyTitle || 'İsimsiz Anket',
            totalResponses: s.totalResponses,
            totalParticipations: s.totalParticipations,
            satisfactionRate: s.satisfactionRate,
            dissatisfactionRate: s.dissatisfactionRate,
            neutralRate: s.neutralRate,
            feedbackCount: s.feedbackCount,
            lastResponseDate: s.lastResponseDate ? new Date(s.lastResponseDate).getTime() : 0,
            averageResponsesPerParticipation: s.averageResponsesPerParticipation,
          }))
          .filter(s => s.totalResponses > 0)
          .sort((a, b) => a.surveyTitle.localeCompare(b.surveyTitle)));
      } catch (error) {
        console.error("Anket istatistikleri yüklenirken hata:", error);
        setOverallStatsData([]);
        setTrendData([]);
        setSurveyDetailedStats([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSurveyStats();

    // Normal görev istatistiklerini de hesapla
    if (normalTasks.length > 0) {
      calculateNormalTaskStats(normalTasks);
    } else {
      calculateNormalTaskStats([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, branchId, isManager, normalTasks, showBranchFilter, selectedBranchFilter]);
  
  // Normal Görev İstatistiklerini Hesapla
  const calculateNormalTaskStats = (tasksToProcess: any[]) => {
    const statusCounts: { [key: string]: number } = {
            pending: 0,
      accepted: 0,
      started: 0,
      completed: 0,
    };

    tasksToProcess.forEach(task => {
      const status = task.status || 'pending';
      if (status in statusCounts) {
        statusCounts[status]++;
        }
    });

    const stats = Object.keys(statusCounts)
      .map(statusKey => ({
        name: statusKey, // Anahtarı doğrudan ata, çeviriyi burada yapma
        value: statusCounts[statusKey],
        color: NORMAL_TASK_STATUS_COLORS[statusKey as keyof typeof NORMAL_TASK_STATUS_COLORS]
      }))
      .filter(stat => stat.value > 0);

    setNormalTaskStatsData(stats);
    
    // Normal görev personel performansını da hesapla
    calculateNormalTaskPersonnelPerformance(tasksToProcess);
  };

  // Normal Görev Personel Performansı Hesapla
  const calculateNormalTaskPersonnelPerformance = (tasksToProcess: any[]) => {
    // Personel bazlı görev sayılarını topla
    const personnelTaskCounts: { [key: string]: { name: string, completed: number, pending: number } } = {};

    tasksToProcess.forEach(task => {
      // Personel ID'si yoksa atla
      if (!task.personnelId) return;
      
      // Personel adını al
      const personnelName = task.personnelName || 'Bilinmeyen Personel';
      
      // Bu personel için kayıt yoksa oluştur
      if (!personnelTaskCounts[task.personnelId]) {
        personnelTaskCounts[task.personnelId] = {
            name: personnelName,
            completed: 0,
          pending: 0
          };
        }
        
      // Görev durumuna göre sayıları artır
        if (task.status === 'completed') {
        personnelTaskCounts[task.personnelId].completed += 1;
      } else {
        // Tamamlanmamış tüm görevleri "pending" olarak kabul et (accepted, started, pending)
        personnelTaskCounts[task.personnelId].pending += 1;
      }
    });
    
    // Personel verilerini diziye dönüştür
    const personnelPerformanceData = Object.values(personnelTaskCounts)
      // Toplam görev sayısına göre sırala (çoktan aza)
      .sort((a, b) => (b.completed + b.pending) - (a.completed + a.pending));
    
    setNormalTaskPersonnelPerformanceData(personnelPerformanceData);
  };

  // Tooltip formatter - genel memnuniyet grafiği için
  const renderCustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 1, border: '1px solid #ccc', borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">{`${payload[0].name}: ${payload[0].value}`}</Typography>
          <Typography variant="body2">
            {`Oran: %${((payload[0].value / overallStatsData.reduce((a, b) => a + b.value, 0)) * 100).toFixed(1)}`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Tooltip formatter - trend grafiği için
  const renderTrendTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 1, border: '1px solid #ccc', borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">{label}</Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Box sx={{ width: 10, height: 10, bgcolor: entry.color, borderRadius: '50%' }} />
              <Typography variant="body2">
                {`${entry.name}: %${entry.value}`}
              </Typography>
            </Box>
          ))}
          <Typography variant="body2" fontSize="11px" color="text.secondary" mt={0.5}>
            {`Toplam: ${payload[0].payload.total} cevap`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Tooltip formatter - normal görev durumu için
  const renderNormalTaskTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = normalTaskStatsData.reduce((sum, entry) => sum + entry.value, 0);
      const percentValue = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 1, border: '1px solid #ccc', borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">
            {`${data.name}: ${data.value} görev`}
          </Typography>
          <Typography variant="body2">
            {`Oran: %${percentValue}`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Normal görev personel performans tooltip
  const renderNormalTaskPersonnelTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 2, boxShadow: 3, borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">{payload[0]?.payload.name}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: NORMAL_TASK_STATUS_COLORS.completed, borderRadius: '50%' }} />
            <Typography variant="body2">{t('dashboard.normalTaskPersonnelPerformanceChart.tooltip.completed')}: {payload[0]?.value}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: NORMAL_TASK_STATUS_COLORS.accepted, borderRadius: '50%' }} />
            <Typography variant="body2">{t('dashboard.normalTaskPersonnelPerformanceChart.tooltip.pending')}: {payload[1]?.value}</Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  // Şube filtresi değişim handler'ı
  const handleBranchFilterChange = (event: SelectChangeEvent<string>) => {
    setSelectedBranchFilter(event.target.value as string);
  };

  return (
    <Box>
      {/* Şube Filtresi - sadece showBranchFilter true ise göster */}
      {showBranchFilter && !isManager && branches.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <FormControl variant="outlined" size="small">
            <InputLabel id="branch-filter-label">Şube</InputLabel>
            <Select
              labelId="branch-filter-label"
              value={selectedBranchFilter}
              label="Şube"
              onChange={handleBranchFilterChange}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">Tüm Şubeler</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Grid container spacing={1}>
        {finalWidgetsToShow.includes('normalTaskStatusChart') && (
          <Grid item xs={12} md={finalWidgetsToShow.includes('overallSurveySatisfaction') || finalWidgetsToShow.includes('surveyTrend') || finalWidgetsToShow.includes('normalTaskPersonnelPerformance') ? 6 : 12}>
            <ChartContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  {t('dashboard.normalTaskStatusChart.title')}
                </Typography>
                <GuideButton guideKey="dashboard.normalTaskStatus" size="small" color="primary" />
              </Box>
              <Divider sx={{ mb: 1 }} />
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <CircularProgress />
                </Box>
              ) : normalTaskStatsData.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography variant="body2" color="textSecondary">{t('dashboard.normalTaskStatusChart.noTasks')}</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={normalTaskStatsData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                    >
                      {normalTaskStatsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={renderNormalTaskTooltip} />
                    <Legend 
                      formatter={(value, entry) => {
                        if (!entry || !entry.payload) return value;
                        const total = normalTaskStatsData.reduce((sum, item) => sum + item.value, 0);
                        const percent = total > 0 ? ((entry.payload.value / total) * 100).toFixed(0) : 0;
                        const translatedValue = t(`dashboard.normalTaskStatusChart.status.${value}`, value);
                        return `${translatedValue} (%${percent})`;
                      }}
                      wrapperStyle={{ fontSize: '12px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartContainer>
          </Grid>
        )}

        {finalWidgetsToShow.includes('normalTaskPersonnelPerformance') && (
          <Grid item xs={12} md={finalWidgetsToShow.includes('normalTaskStatusChart') || finalWidgetsToShow.includes('overallSurveySatisfaction') || finalWidgetsToShow.includes('surveyTrend') ? 6 : 12}>
            <ChartContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  {t('dashboard.normalTaskPersonnelPerformanceChart.title')}
                </Typography>
                <GuideButton guideKey="dashboard.normalTaskPersonnelPerformance" size="small" color="primary" />
              </Box>
              <Divider sx={{ mb: 1 }} />
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                  <CircularProgress />
                </Box>
              ) : normalTaskPersonnelPerformanceData.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                  <Typography variant="body2" color="textSecondary">{t('dashboard.normalTaskPersonnelPerformanceChart.noData')}</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={normalTaskPersonnelPerformanceData.slice(0, 5)}
                    margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      tick={{ fontSize: 12 }}
                      width={180}
                      tickFormatter={(value) => {
                        // Uzun isimleri kısaltma olmadan göster
                        return value;
                      }}
                    />
                    <RechartsTooltip content={renderNormalTaskPersonnelTooltip} />
                    <Legend verticalAlign="bottom" height={24} />
                    <Bar 
                      dataKey="completed" 
                      stackId="a" 
                      fill={NORMAL_TASK_STATUS_COLORS.completed} 
                      name={t('dashboard.normalTaskPersonnelPerformanceChart.legend.completed')} 
                      barSize={20}
                    >
                      <LabelList dataKey="completed" position="inside" formatter={(value: number) => value > 0 ? value : ''} style={{ fill: 'white' }} />
                    </Bar>
                    <Bar 
                      dataKey="pending" 
                      stackId="a" 
                      fill={NORMAL_TASK_STATUS_COLORS.accepted} 
                      name={t('dashboard.normalTaskPersonnelPerformanceChart.legend.pending')}
                      barSize={20}
                    >
                      <LabelList dataKey="pending" position="inside" formatter={(value: number) => value > 0 ? value : ''} style={{ fill: 'white' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartContainer>
          </Grid>
        )}

        {finalWidgetsToShow.includes('overallSurveySatisfaction') && (
          <Grid item xs={12} md={finalWidgetsToShow.includes('normalTaskStatusChart') || finalWidgetsToShow.includes('surveyTrend') ? 6 : 12}>
            <ChartContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  Genel Anket Memnuniyet Oranı
                </Typography>
                <GuideButton guideKey="dashboard.surveySatisfaction" size="small" color="primary" />
              </Box>
              <Divider sx={{ mb: 1 }} />
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <CircularProgress />
                </Box>
              ) : overallStatsData.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography variant="body2" color="textSecondary">Henüz anket verisi bulunmuyor</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={overallStatsData}
                    margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip content={renderCustomTooltip} />
                    <Legend verticalAlign="bottom" height={36} />
                    <Bar dataKey="value" name="Cevap Sayısı" barSize={30}>
                      {overallStatsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartContainer>
          </Grid>
        )}
        
        {finalWidgetsToShow.includes('surveyTrend') && (
          <Grid item xs={12} md={finalWidgetsToShow.includes('normalTaskStatusChart') || finalWidgetsToShow.includes('overallSurveySatisfaction') ? 6 : 12}>
            <ChartContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  Son 1 Ay Memnuniyet Trendi
                </Typography>
                <GuideButton guideKey="dashboard.surveyTrend" size="small" color="primary" />
              </Box>
              <Divider sx={{ mb: 1 }} />
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <CircularProgress />
                </Box>
              ) : trendData.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography variant="body2" color="textSecondary">Henüz anket verisi bulunmuyor</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={trendData}
                    margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="week" />
                    <YAxis label={{ value: 'Yüzde (%)', angle: -90, position: 'insideLeft' }} />
                    <RechartsTooltip content={renderTrendTooltip} />
                    <Legend verticalAlign="bottom" height={36} />
                    <Bar dataKey="positivePercent" name="Olumlu" fill={SURVEY_COLORS.positive} stackId="a" barSize={30} />
                    <Bar dataKey="neutralPercent" name="Nötr" fill={SURVEY_COLORS.neutral} stackId="a" barSize={30} />
                    <Bar dataKey="negativePercent" name="Olumsuz" fill={SURVEY_COLORS.negative} stackId="a" barSize={30} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartContainer>
          </Grid>
        )}

        {finalWidgetsToShow.includes('surveyDetailedStats') && (
          <Grid item xs={12}>
            <ChartContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  Anket Bazlı Detaylı İstatistikler
                </Typography>
                <GuideButton guideKey="dashboard.surveyDetailedStats" size="small" color="primary" />
              </Box>
              <Divider sx={{ mb: 1 }} />
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <CircularProgress />
                </Box>
              ) : surveyDetailedStats.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <Typography variant="body2" color="textSecondary">Henüz anket verisi bulunmuyor</Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {/* Toplam Cevap Sayıları */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Anket Cevap Sayıları
                      </Typography>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart
                          data={surveyDetailedStats}
                          margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="surveyTitle" 
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fontSize: 10 }}
                          />
                          <YAxis label={{ value: 'Cevap Sayısı', angle: -90, position: 'insideLeft' }} />
                          <RechartsTooltip 
                            formatter={(value, name) => [
                              value,
                              'Toplam Cevap Sayısı'
                            ]}
                            labelFormatter={(label) => `Anket: ${label}`}
                          />
                          <Legend />
                          <Bar 
                            dataKey="totalResponses" 
                            name="Toplam Cevap"
                            fill="#F44336"
                            radius={[4, 4, 0, 0]}
                          >
                            {surveyDetailedStats.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.totalResponses >= 50 ? '#4CAF50' : entry.totalResponses >= 20 ? '#FF9800' : '#F44336'} 
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>

                  {/* Memnuniyet Dağılımı Pasta Grafik */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Toplam Memnuniyet Dağılımı
                      </Typography>
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={[
                              { 
                                name: 'Memnun', 
                                value: surveyDetailedStats.reduce((sum, stat) => sum + stat.satisfactionRate, 0) / surveyDetailedStats.length || 0,
                                color: SURVEY_COLORS.positive 
                              },
                              { 
                                name: 'Nötr', 
                                value: surveyDetailedStats.reduce((sum, stat) => sum + stat.neutralRate, 0) / surveyDetailedStats.length || 0,
                                color: SURVEY_COLORS.neutral 
                              },
                              { 
                                name: 'Memnun Değil', 
                                value: surveyDetailedStats.reduce((sum, stat) => sum + stat.dissatisfactionRate, 0) / surveyDetailedStats.length || 0,
                                color: SURVEY_COLORS.negative 
                              }
                            ]}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            fill="#8884d8"
                            label={({ value }) => `%${Math.round(value)}`}
                          >
                            {[
                              { color: SURVEY_COLORS.positive },
                              { color: SURVEY_COLORS.neutral },
                              { color: SURVEY_COLORS.negative }
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip formatter={(value) => [`%${Math.round(Number(value))}`, '']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>

                  {/* Anket Performans Skor Kartları ve Grafik */}
                  <Grid item xs={12}>
                    <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Anket Performans Skorları
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {/* Sol taraf - Performans Kartları */}
                        <Grid item xs={12} md={6}>
                          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                            {surveyDetailedStats.map((stat, index) => (
                              <Box 
                                key={stat.surveyId} 
                                sx={{ 
                                  mb: 2, 
                                  p: 2, 
                                  border: '1px solid', 
                                  borderColor: 'divider', 
                                  borderRadius: 1,
                                  bgcolor: 'background.default'
                                }}
                              >
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  {stat.surveyTitle}
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={4}>
                                    <Box textAlign="center">
                                      <Typography variant="h6" color="primary.main" fontWeight="bold">
                                        {stat.totalResponses}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Toplam Cevap
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Box textAlign="center">
                                      <Typography variant="h6" color="success.main" fontWeight="bold">
                                        %{stat.satisfactionRate}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Memnuniyet
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Box textAlign="center">
                                      <Typography variant="h6" color="error.main" fontWeight="bold">
                                        %{stat.dissatisfactionRate}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Olumsuz
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                                {stat.lastResponseDate > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Son cevap: {new Date(stat.lastResponseDate).toLocaleDateString('tr-TR')}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Grid>

                        {/* Sağ taraf - Performans Grafiği */}
                        <Grid item xs={12} md={6}>
                          <Box sx={{ height: 400 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom align="center">
                              Anket Performans Karşılaştırması
                            </Typography>
                            {surveyDetailedStats.length > 0 ? (
                              <ResponsiveContainer width="100%" height="90%">
                                <BarChart
                                  data={surveyDetailedStats.map(stat => ({
                                    name: stat.surveyTitle.length > 15 
                                      ? `${stat.surveyTitle.substring(0, 15)}...` 
                                      : stat.surveyTitle,
                                    fullName: stat.surveyTitle,
                                    memnuniyet: stat.satisfactionRate,
                                    olumsuz: stat.dissatisfactionRate,
                                    notr: stat.neutralRate,
                                    toplam: stat.totalResponses
                                  }))}
                                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis 
                                    dataKey="name" 
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                    fontSize={12}
                                  />
                                  <YAxis 
                                    domain={[0, 100]}
                                    label={{ value: 'Yüzde (%)', angle: -90, position: 'insideLeft' }}
                                  />
                                  <RechartsTooltip 
                                    formatter={(value: any, name: any) => {
                                      const displayName = name === 'memnuniyet' ? 'Memnuniyet' :
                                                         name === 'olumsuz' ? 'Olumsuz' :
                                                         name === 'notr' ? 'Nötr' : name;
                                      return [`%${value}`, displayName];
                                    }}
                                    labelFormatter={(label: any, payload: any) => {
                                      if (payload && payload[0] && payload[0].payload) {
                                        const data = payload[0].payload;
                                        return `${data.fullName} (${data.toplam} cevap)`;
                                      }
                                      return label;
                                    }}
                                  />
                                  <Legend />
                                  <Bar 
                                    dataKey="memnuniyet" 
                                    name="Memnuniyet" 
                                    fill={SURVEY_COLORS.positive}
                                    radius={[2, 2, 0, 0]}
                                  />
                                  <Bar 
                                    dataKey="notr" 
                                    name="Nötr" 
                                    fill={SURVEY_COLORS.neutral}
                                    radius={[2, 2, 0, 0]}
                                  />
                                  <Bar 
                                    dataKey="olumsuz" 
                                    name="Olumsuz" 
                                    fill={SURVEY_COLORS.negative}
                                    radius={[2, 2, 0, 0]}
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'center', 
                                  alignItems: 'center', 
                                  height: '90%',
                                  color: 'text.secondary' 
                                }}
                              >
                                <Typography variant="body2">
                                  Anket performans verisi bulunamadı
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </ChartContainer>
          </Grid>
        )}

      </Grid>
    </Box>
  );
};

export default SurveyCharts; 